// API client for Vercel deployment (without workspace dependency)
import axios, { AxiosInstance } from 'axios';
import type { TournamentRole, ParticipantStatus, InviteStatus, MessageType, EmailDeliveryStatus, TournamentFavoriteList } from '@/constants';
import type {
  League,
  LeagueDisableImpact,
  LeagueFilterOptions,
  LeagueMatchesResponse,
  LeagueStandingsResponse,
  LeagueTournamentsResponse,
  MatchesByDateResponse,
  VisibleLeaguesQuery,
  VisibleLeaguesResponse,
} from '@/types/league';
import type { MatchOverview, MatchTournamentsResponse } from '@/types/matchOverview';
import type { TournamentListParams } from '@/types/tournamentList';
import type {
  AcceptInviteLinkResult,
  CreateInviteLinkData,
  InviteLink,
  InviteLinkPreview,
} from '@/types/inviteLink';

// Mark requests that have already been retried after a token refresh so we don't loop on a second 401
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    __isRetry?: boolean;
  }
  export interface AxiosRequestConfig {
    __isRetry?: boolean;
  }
}

// Tournament invitation types
export interface EmailInvitationData {
  tournamentId: number;
  email: string;
  message?: string;
  role?: TournamentRole;
}

export interface BulkEmailInvitationData {
  tournamentId: number;
  emails: string[];
  message?: string;
  role?: TournamentRole;
}

export interface UsernameInvitationData {
  tournamentId: number;
  inviteeUsername: string;
  message?: string;
  role?: TournamentRole;
}

export interface UserMessage {
  id: number;
  user_id: number;
  message_type: MessageType;
  title: string;
  content: string;
  related_tournament_id?: number;
  related_invitation_id?: number;
  action_url?: string;
  action_label?: string;
  is_read: boolean;
  is_archived: boolean;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  email_sent: boolean;
  email_sent_at?: string;
  email_delivery_status: EmailDeliveryStatus;
  created_at: string;
  updated_at: string;
  expires_at?: string;
  tournament_name?: string;
  competition_name?: string;
  league_name?: string;
  invitation_status?: InviteStatus;
  invitation_accepted_at?: string;
  invitation_declined_at?: string;
}

export interface UserMessagesOptions {
  messageType?: MessageType;
  isRead?: boolean;
  isArchived?: boolean;
  page?: number;
  limit?: number;
  sortBy?: 'created_at' | 'updated_at' | 'priority';
  sortOrder?: 'ASC' | 'DESC';
}

export interface UserMessagesResponse {
  messages: UserMessage[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Tournament participants management types
export interface TournamentParticipant {
  id: number;
  tournament_id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  username: string | null;
  email: string;
  role: TournamentRole;
  status: ParticipantStatus;
  joined_at: string;
  last_activity_at: string | null;
  total_predictions: number;
  total_points: number;
  blocked_at: string | null;
  block_reason: string | null;
  blocked_by_first_name: string | null;
  blocked_by_last_name: string | null;
  days_since_last_activity: number | null;
  leaderboard_position: number;
}

export interface ParticipantStats {
  total_participants: number;
  active_participants: number;
  blocked_participants: number;
  awaiting_participants: number;
  active_predictors: number;
  avg_predictions_per_user: number;
}

export interface ParticipantsManagementOptions {
  status?: ParticipantStatus;
  role?: TournamentRole;
  sortBy?: 'leaderboard_position' | 'joined_at' | 'last_activity_at' | 'total_points';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface BlockToggleData {
  block: boolean;
  reason: string;
}

// User search types
export interface UserSearchResult {
  id: number;
  first_name: string;
  last_name: string;
  username: string | null;
  email: string;
  display_name: string;
  email_verified: boolean;
  account_status: string;
  created_at: string;
  tournament_participation?: {
    role: string;
    status: string;
    joined_at: string;
    is_participant: boolean;
  } | {
    is_participant: false;
  };
}

export interface UserSearchOptions {
  q: string;
  limit?: number;
  exclude?: number[];
  tournament_id?: number;
}

export interface UserSearchResponse {
  success: boolean;
  data: UserSearchResult[];
  meta: {
    query: string;
    total_results: number;
    limit: number;
    has_more: boolean;
  };
}

export interface UserSuggestionsResponse {
  success: boolean;
  data: Array<UserSearchResult & { suggestion_reason: string }>;
  meta: {
    total_results: number;
    limit: number;
    suggestion_type: 'tournament_based' | 'general';
  };
}

const API_URL = import.meta.env?.VITE_API_URL;

if (!API_URL) {
  throw new Error('VITE_API_URL environment variable is required');
}

// ============================================================
// Participant breakdown — per-participant statistics page
// (`GET /api/leaderboard/:tournamentId/user/:userId/breakdown`)
// ============================================================

export interface ParticipantBreakdownTournament {
  id: number;
  name: string;
  status: string;
}

export interface ParticipantBreakdownParticipant {
  user_id: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  position: number;
  total_points: number;
  total_predictions: number;
  correct_predictions: number;
  exact_predictions: number;
}

export interface ParticipantBreakdownTeam {
  id: number;
  name: string;
  logo_url: string | null;
}

export interface ParticipantBreakdownPrediction {
  predicted_score_team1: number;
  predicted_score_team2: number;
  predicted_outcome: 'team1_win' | 'draw' | 'team2_win';
  odds_score: number;
}

export interface ParticipantBreakdownCoefficients {
  team1_win_coefficient: number | null;
  draw_coefficient: number | null;
  team2_win_coefficient: number | null;
  selection: 'manual' | 'priority' | 'none';
  source_bookmaker: string | null;
}

export interface ParticipantBreakdownPointsBreakdown {
  score_points: number;
  result_points: number;
  total_points: number;
}

export interface ParticipantBreakdownMatch {
  match_id: number;
  match_date: string;
  status: string;
  stage: string | null;
  matchday: number | null;
  team1: ParticipantBreakdownTeam;
  team2: ParticipantBreakdownTeam;
  actual_score: { team1: number; team2: number } | null;
  prediction: ParticipantBreakdownPrediction | null;
  coefficients: ParticipantBreakdownCoefficients;
  points_breakdown: ParticipantBreakdownPointsBreakdown | null;
}

export interface ParticipantBreakdownData {
  tournament: ParticipantBreakdownTournament;
  participant: ParticipantBreakdownParticipant;
  is_own_profile: boolean;
  matches: ParticipantBreakdownMatch[];
}

interface ExtendedAxiosInstance extends AxiosInstance {
  // Auth methods
  loginWithEmail: (email: string, password: string) => Promise<any>;
  registerWithEmail: (email: string, password: string, firstName: string) => Promise<any>;
  loginWithTelegram: (initData: string) => Promise<any>;
  loginWithGoogle: (profile: any) => Promise<any>;
  loginWithApple: (profile: any) => Promise<any>;
  register: (data: any) => Promise<any>;
  registerWithTelegram: (data: any) => Promise<any>;
  registerWithGoogle: (data: any) => Promise<any>;
  registerWithApple: (data: any) => Promise<any>;
  linkTelegramWithEmail: (data: any) => Promise<any>;
  linkTelegramToAccount: (data: any) => Promise<any>;
  logout: () => Promise<any>;
  
  // Token methods
  getTokens: () => any;
  setTokens: (tokens: any) => void;
  
  // API methods
  getTournaments: (params?: TournamentListParams, signal?: AbortSignal) => Promise<any>;
  getTournament: (id: string) => Promise<any>;
  getMatch: (id: string) => Promise<any>;
  getNextToPredict: (excludeIds?: number[]) => Promise<any>;
  getPublicTournaments: (params?: TournamentListParams, signal?: AbortSignal) => Promise<any>;
  createUserTournament: (data: any) => Promise<any>;
  updateTournamentSettings: (tournamentId: number, data: any) => Promise<any>;
  archiveTournament: (tournamentId: number) => Promise<any>;
  getArchivedTournaments: (params?: TournamentListParams, signal?: AbortSignal) => Promise<any>;
  unarchiveTournament: (tournamentId: number) => Promise<any>;
  setTournamentFavorite: (tournamentId: number, list: TournamentFavoriteList) => Promise<any>;
  removeTournamentFavorite: (tournamentId: number, list: TournamentFavoriteList) => Promise<any>;
  createPrediction: (data: any) => Promise<any>;
  updatePrediction: (id: string, data: any) => Promise<any>;
  createBulkPredictions: (data: any) => Promise<any>;
  getTournamentsByLeague: (leagueId: number) => Promise<any>;
  getTournamentMatches: (id: string, matchday?: number, all?: boolean) => Promise<any>;
  getTournamentMatch: (tournamentId: string, matchId: string) => Promise<any>;
  getTournamentLeaderboard: (id: string, limit?: number) => Promise<any>;
  getGlobalLeaderboard: (limit?: number) => Promise<any>;
  getUserPosition: (tournamentId: string, userId: string) => Promise<any>;
  getParticipantBreakdown: (tournamentId: string, userId: string) => Promise<{ data: { data: ParticipantBreakdownData } }>;
  getMyPredictions: (tournamentId?: string) => Promise<any>;
  updateMatchCoefficients: (tournamentId: string, matchId: string, coefficients: any) => Promise<any>;
  getMatchCoefficients: (tournamentId: string, matchId: string) => Promise<any>;
  getMatchOdds: (tournamentId: string, matchId: string, score?: string) => Promise<any>;

  // Admin methods
  getAdminSettings: () => Promise<any>;
  updateAdminSettings: (settings: any) => Promise<any>;
  getDebugMode: () => Promise<any>;
  
  // Email verification methods
  verifyEmail: (token: string) => Promise<any>;
  resendEmailVerification: () => Promise<any>;
  getVerificationStatus: () => Promise<any>;

  // Password methods
  forgotPassword: (email: string) => Promise<any>;
  validateResetToken: (token: string) => Promise<any>;
  resetPassword: (token: string, newPassword: string) => Promise<any>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<any>;

  // Geo data endpoints
  getCountries: () => Promise<any>;
  getCities: (countryIsoCode: string) => Promise<any>;

  // Admin user management methods
  getAdminUsers: (filters?: any) => Promise<any>;
  getAdminUserDetails: (userId: number) => Promise<any>;
  blockUser: (userId: number, reason: string) => Promise<any>;
  unblockUser: (userId: number) => Promise<any>;
  adminSendVerification: (userId: number) => Promise<any>;
  updateUserRole: (userId: number, role: string, isAdmin: boolean) => Promise<any>;
  getAdminAuditLogs: (filters?: any) => Promise<any>;
  getEmailVerificationLogs: (filters?: any) => Promise<any>;
  getAdminStats: () => Promise<any>;
  
  // OAuth linking methods
  linkGoogleAccount: (token: string) => Promise<any>;
  linkAppleAccount: (identityToken: string, authorizationCode?: string, userData?: any) => Promise<any>;
  getOAuthProviders: () => Promise<any>;
  removeOAuthProvider: (provider: string) => Promise<any>;
  
  // Tournament invitation methods
  sendEmailInvitation: (data: EmailInvitationData) => Promise<any>;
  sendBulkEmailInvitations: (data: BulkEmailInvitationData) => Promise<any>;
  sendUsernameInvitation: (data: UsernameInvitationData) => Promise<any>;
  acceptInvitation: (inviteCode: string) => Promise<any>;
  declineInvitation: (inviteCode: string) => Promise<any>;
  getTournamentInvitations: (tournamentId: number, status?: string) => Promise<any>;
  getInvitationPreview: (inviteCode: string) => Promise<any>;
  revokeInvitation: (inviteCode: string) => Promise<any>;

  // Tournament invite links (link / QR) methods
  createInviteLink: (tournamentId: number, data: CreateInviteLinkData) => Promise<{ data: { data: InviteLink } }>;
  getInviteLinks: (tournamentId: number) => Promise<{ data: { data: InviteLink[] } }>;
  revokeInviteLink: (tournamentId: number, linkId: number) => Promise<unknown>;
  deleteInviteLink: (tournamentId: number, linkId: number) => Promise<unknown>;
  getInviteLinkPreview: (token: string) => Promise<{ data: { data: InviteLinkPreview } }>;
  acceptInviteLink: (token: string) => Promise<{ data: { data: AcceptInviteLinkResult } }>;
  
  // User messages methods
  getUserMessages: (options?: UserMessagesOptions) => Promise<any>;
  getUnreadMessageCount: () => Promise<any>;
  getMessage: (messageId: number) => Promise<any>;
  markMessageAsRead: (messageId: number) => Promise<any>;
  markAllMessagesAsRead: (messageType?: string) => Promise<any>;
  archiveMessage: (messageId: number) => Promise<any>;
  
  // Tournament participants management methods
  getTournamentParticipantsManagement: (tournamentId: number, options?: ParticipantsManagementOptions) => Promise<any>;
  getTournamentParticipantsStats: (tournamentId: number) => Promise<any>;
  toggleTournamentParticipantBlock: (tournamentId: number, userId: number, data: BlockToggleData) => Promise<any>;
  deleteTournamentParticipant: (tournamentId: number, userId: number) => Promise<any>;
  updateParticipantRole: (tournamentId: number, userId: number, role: string) => Promise<any>;

  // User search methods
  searchUsers: (options: UserSearchOptions) => Promise<any>;

  // Admin match sync methods
  fullSyncAllMatches: () => Promise<any>;
  startLeagueFullSync: (leagueId: number) => Promise<any>;
  getLeagueFullSyncStatus: () => Promise<any>;

  // Token verification
  verifyToken: () => Promise<any>;

  // Leagues API methods
  getAvailableLeagues: () => Promise<any>;
  getAdminLeagues: (params?: Record<string, string>) => Promise<any>;
  getAdminLeagueFilterOptions: () => Promise<any>;
  syncLeaguesFromApi: (mode: string) => Promise<any>;
  toggleLeagueEnabled: (leagueId: number, isEnabled: boolean) => Promise<any>;
  getLeagueDisableImpact: (leagueId: number) => Promise<{ data: { data: LeagueDisableImpact } }>;

  // Public league browser (`/matches`) methods
  getVisibleLeagues: (params?: VisibleLeaguesQuery) => Promise<{ data: { data: VisibleLeaguesResponse } }>;
  getVisibleLeagueFilterOptions: () => Promise<{ data: { data: LeagueFilterOptions } }>;
  getLeague: (leagueId: string | number) => Promise<{ data: { data: League } }>;
  getLeagueMatches: (leagueId: string | number, opts?: { all?: boolean; matchday?: number }) => Promise<{ data: LeagueMatchesResponse }>;
  /** `date` is a local calendar day in `YYYY-MM-DD`. */
  getMatchesByDate: (date: string) => Promise<{ data: MatchesByDateResponse }>;
  getLeagueStandings: (leagueId: string | number) => Promise<{ data: { data: LeagueStandingsResponse } }>;
  getLeagueTournaments: (leagueId: string | number) => Promise<{ data: { data: LeagueTournamentsResponse } }>;

  // Standalone match page (`/match/:matchId`) methods
  getMatchOverview: (matchId: string | number) => Promise<{ data: { data: MatchOverview } }>;
  getMatchTournaments: (matchId: string | number) => Promise<{ data: { data: MatchTournamentsResponse } }>;

  // Bookmakers (admin) API methods
  getAdminBookmakers: (params?: Record<string, string>) => Promise<any>;
  toggleBookmakerEnabled: (bookmakerId: number, isEnabled: boolean) => Promise<any>;

  // Admin League ↔ Bookmakers mapping methods
  getAdminLeagueBookmakers: () => Promise<any>;
  getAdminLeagueBookmakerDetail: (leagueId: number) => Promise<any>;
  saveAdminLeagueBookmakers: (leagueId: number, bookmakerIds: number[]) => Promise<any>;
  clearAdminLeagueBookmakers: (leagueId: number) => Promise<any>;
  discoverLeagueBookmakers: (leagueId: number) => Promise<any>;

  // Teams API methods
  getTeamsByCompetition: (leagueCode: string) => Promise<any>;
  getTeamById: (teamId: string) => Promise<any>;
  getTeamShortNames: (teamNames: string[]) => Promise<any>;

  // Tournament statistics methods
  getTournamentStatistics: (tournamentId: string, matchday?: number, stage?: string) => Promise<any>;

  // Match predictions methods
  getMatchPredictions: (tournamentId: string, matchId: string) => Promise<any>;

  // User profile management
  updateUserProfile: (profileData: any) => Promise<any>;
  uploadAvatar: (file: File) => Promise<any>;
  removeAvatar: () => Promise<any>;

  // Unified account system endpoints
  checkEmail: (email: string) => Promise<any>;
  registerTelegramEmail: (email: string, password: string, telegramData: any) => Promise<any>;
  linkTelegram: (email: string, password: string, telegramData: any) => Promise<any>;
  getAccountLinks: () => Promise<any>;
  linkOAuthProvider: (provider: string, oauthToken: string, email?: string, password?: string) => Promise<any>;
  unlinkProvider: (provider: string) => Promise<any>;
  deleteAccount: (password: string) => Promise<any>;
  registerTelegramOnly: (telegramData: any) => Promise<any>;
  bindEmail: (email: string, password: string) => Promise<any>;
}

const baseClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
}) as ExtendedAxiosInstance;

// Routes where the user is anonymous-friendly — do not redirect away from them on auth errors
const ANONYMOUS_PATHS = new Set(['/login', '/sign-up', '/landing', '/']);
// Prefixes of anonymous-friendly routes (the invite-link landing works signed in or not).
const ANONYMOUS_PATH_PREFIXES = ['/join/'];

function isAnonymousPath(pathname: string): boolean {
  return ANONYMOUS_PATHS.has(pathname) || ANONYMOUS_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

// Single-flight refresh: while a refresh is in progress, parallel 401s wait on the same promise
let pendingRefresh: Promise<string | null> | null = null;

async function performTokenRefresh(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  try {
    // Use a bare axios call to avoid being intercepted recursively
    const response = await axios.post(`${API_URL}/auth/refresh`, { refresh_token: refreshToken });
    const payload = response.data?.data || response.data;
    const tokens = payload?.tokens || payload;
    const newAccess = tokens?.accessToken || tokens?.access_token || tokens?.token;
    const newRefresh = tokens?.refreshToken || tokens?.refresh_token;

    if (!newAccess) return null;
    localStorage.setItem('accessToken', newAccess);
    if (newRefresh) localStorage.setItem('refreshToken', newRefresh);
    return newAccess;
  } catch {
    return null;
  }
}

function clearAuthAndRedirect() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('currentUser');
  localStorage.removeItem('lastActivity');

  const currentPath = window.location.pathname;
  if (isAnonymousPath(currentPath)) {
    // User is already on an anonymous-friendly page — no redirect needed (avoids loops)
    return;
  }

  // Preserve where the user was so LoginPage can route them back after auth
  const from = encodeURIComponent(currentPath + window.location.search);
  window.location.href = `/login?from=${from}`;
}

// Add request interceptor to include auth token
baseClient.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    const lang = localStorage.getItem('pclash-language') || 'en';
    config.headers['Accept-Language'] = lang;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle expired tokens and blocked accounts
baseClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Check if account is blocked
    const isAccountBlocked = error.response?.status === 403 &&
                             error.response?.data?.error === 'account_blocked';

    if (isAccountBlocked) {
      // Get the request URL to determine context
      const requestUrl = error.config?.url || '';

      // Don't redirect if this is a login/register attempt (user isn't logged in yet)
      const isAuthAttempt = requestUrl.includes('/auth/login') ||
                           requestUrl.includes('/auth/register') ||
                           requestUrl.includes('/auth/telegram');

      if (isAuthAttempt) {
        // For login attempts, just return the error to be handled by the form
        return Promise.reject(error);
      }

      // For already logged-in users who got blocked, redirect to blocked page
      // Clear auth data
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('lastActivity');

      // Store blocked reason for display on blocked page
      const blockedReason = error.response?.data?.blocked_reason;
      const blockedAt = error.response?.data?.blocked_at;
      if (blockedReason) {
        localStorage.setItem('blockedReason', blockedReason);
      }
      if (blockedAt) {
        localStorage.setItem('blockedAt', blockedAt);
      }

      // Redirect to account blocked page
      window.location.href = '/account-blocked';
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const data = error.response?.data;
    const requestUrl: string = error.config?.url || '';

    // Detect auth errors — tolerant to both old (403 + message) and new (401 + code) backend shapes
    const newCodeIsAuth = status === 401 &&
                          (data?.code === 'TOKEN_EXPIRED' ||
                           data?.code === 'TOKEN_INVALID' ||
                           data?.code === 'NO_TOKEN');
    const legacyMessageIsAuth = data?.message === 'Invalid token' ||
                                data?.message === 'No token provided' ||
                                data?.message === 'User not found' ||
                                data?.error === 'Invalid or expired token';
    const isAuthError = newCodeIsAuth || legacyMessageIsAuth;

    // Permission error: 403 with explicit access-denied semantics — user is authenticated, just lacks rights
    const isPermissionError = status === 403 &&
                              (data?.error === 'Access denied' ||
                               data?.message?.includes('Only tournament') ||
                               data?.message?.includes('participants can view'));

    if ((status === 401 || status === 403) && isAuthError && !isPermissionError) {
      const originalRequest = error.config;
      const isRefreshCall = requestUrl.includes('/auth/refresh');
      const alreadyRetried = originalRequest?.__isRetry === true;
      const hasRefreshToken = !!localStorage.getItem('refreshToken');

      // Try refresh-token recovery before logging the user out
      if (!isRefreshCall && !alreadyRetried && hasRefreshToken && originalRequest) {
        if (!pendingRefresh) {
          pendingRefresh = performTokenRefresh().finally(() => {
            pendingRefresh = null;
          });
        }

        const newAccessToken = await pendingRefresh;
        if (newAccessToken) {
          // Replay the original request once with the fresh token
          originalRequest.__isRetry = true;
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return baseClient(originalRequest);
        }
      }

      // Refresh failed or unavailable → clear and bounce to /login (preserving return path)
      const hasToken = localStorage.getItem('accessToken');
      if (hasToken || hasRefreshToken) {
        clearAuthAndRedirect();
      }
    }
    return Promise.reject(error);
  }
);

// Add all required methods
baseClient.loginWithEmail = async (email: string, password: string) => {
  return baseClient.post('/auth/login', { email, password });
};

baseClient.registerWithEmail = async (email: string, password: string, firstName: string) => {
  return baseClient.post('/auth/register', { email, password, first_name: firstName });
};

baseClient.loginWithTelegram = async (initData: string) => {
  return baseClient.post('/auth/telegram', { initData });
};

baseClient.loginWithGoogle = async (profile: any) => {
  return baseClient.post('/auth/google', { profile });
};

baseClient.loginWithApple = async (profile: any) => {
  return baseClient.post('/auth/apple', { profile });
};

baseClient.register = async (data: any) => {
  return baseClient.post('/auth/register', data);
};

baseClient.registerWithTelegram = async (initData: string) => {
  return baseClient.post('/auth/telegram', { initData });
};

baseClient.registerWithGoogle = async (data: any) => {
  return baseClient.post('/auth/google/register', data);
};

baseClient.registerWithApple = async (data: any) => {
  return baseClient.post('/auth/apple/register', data);
};

baseClient.linkTelegramWithEmail = async (data: any) => {
  return baseClient.post('/auth/link-telegram', data);
};

baseClient.linkTelegramToAccount = async (data: any) => {
  return baseClient.post('/auth/link-telegram-to-account', data);
};

baseClient.verifyToken = async () => {
  return baseClient.get('/auth/verify');
};

baseClient.logout = async () => {
  try {
    await baseClient.post('/auth/logout');
  } catch (error) {
    console.warn('Logout API call failed:', error);
  } finally {
    // Always clear local storage regardless of API response
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('lastActivity');
  }
};

baseClient.getTokens = () => {
  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');
  return { 
    accessToken, 
    access_token: accessToken, // For backward compatibility
    refreshToken, 
    refresh_token: refreshToken 
  };
};

baseClient.setTokens = (tokens: any) => {
  const accessToken = tokens.accessToken || tokens.access_token || tokens.token;
  const refreshToken = tokens.refreshToken || tokens.refresh_token;
  
  if (accessToken) localStorage.setItem('accessToken', accessToken);
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
};

// Serializes TournamentListParams; '' when no param is set, so a call without
// params hits the legacy (unpaginated) response.
const tournamentListQuery = (params?: TournamentListParams): string => {
  if (!params) return '';
  const search = new URLSearchParams();
  (Object.keys(params) as (keyof TournamentListParams)[]).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

baseClient.getTournaments = async (params?: TournamentListParams, signal?: AbortSignal) => {
  return baseClient.get(`/tournaments${tournamentListQuery(params)}`, { signal });
};

baseClient.getTournament = async (id: string) => {
  return baseClient.get(`/tournaments/${id}`);
};

baseClient.getMatch = async (id: string) => {
  return baseClient.get(`/matches/${id}`);
};

baseClient.getNextToPredict = async (excludeIds?: number[]) => {
  const query = excludeIds?.length ? `?exclude=${excludeIds.join(',')}` : '';
  return baseClient.get(`/matches/next-to-predict${query}`);
};

baseClient.getPublicTournaments = async (params?: TournamentListParams, signal?: AbortSignal) => {
  return baseClient.get(`/public/tournaments${tournamentListQuery(params)}`, { signal });
};

baseClient.createUserTournament = async (data: any) => {
  return baseClient.post('/tournaments', data);
};

baseClient.updateTournamentSettings = async (tournamentId: number, data: any) => {
  return baseClient.put(`/tournaments/${tournamentId}`, data);
};

baseClient.archiveTournament = async (tournamentId: number) => {
  return baseClient.put(`/tournaments/${tournamentId}/archive`);
};

baseClient.getArchivedTournaments = async (params?: TournamentListParams, signal?: AbortSignal) => {
  return baseClient.get(`/tournaments/archived${tournamentListQuery(params)}`, { signal });
};

baseClient.unarchiveTournament = async (tournamentId: number) => {
  return baseClient.put(`/tournaments/${tournamentId}/unarchive`);
};

baseClient.setTournamentFavorite = async (tournamentId: number, list: TournamentFavoriteList) => {
  return baseClient.put(`/tournaments/${tournamentId}/favorites/${list}`);
};

baseClient.removeTournamentFavorite = async (tournamentId: number, list: TournamentFavoriteList) => {
  return baseClient.delete(`/tournaments/${tournamentId}/favorites/${list}`);
};

baseClient.createPrediction = async (data: any) => {
  return baseClient.post('/predictions', data);
};

baseClient.updatePrediction = async (id: string, data: any) => {
  return baseClient.put(`/predictions/${id}`, data);
};

baseClient.createBulkPredictions = async (data: any) => {
  return baseClient.post('/predictions/bulk', data);
};

baseClient.getTournamentsByLeague = async (leagueId: number) => {
  return baseClient.get(`/tournaments/league/${leagueId}`);
};

baseClient.getTournamentMatches = async (id: string, matchday?: number, all?: boolean) => {
  let params = '';
  if (all) {
    params = '?all=true';
  } else if (matchday) {
    params = `?matchday=${matchday}`;
  }
  return baseClient.get(`/tournaments/${id}/matches${params}`);
};

baseClient.getTournamentMatch = async (tournamentId: string, matchId: string) => {
  return baseClient.get(`/tournaments/${tournamentId}/matches/${matchId}`);
};

baseClient.getTournamentLeaderboard = async (id: string, limit?: number) => {
  const params = limit ? `?limit=${limit}` : '';
  return baseClient.get(`/leaderboard/${id}${params}`);
};

baseClient.getGlobalLeaderboard = async (limit?: number) => {
  const params = limit ? `?limit=${limit}` : '';
  return baseClient.get(`/leaderboard${params}`);
};

baseClient.getUserPosition = async (tournamentId: string, userId: string) => {
  return baseClient.get(`/leaderboard/${tournamentId}/user/${userId}`);
};

baseClient.getParticipantBreakdown = async (tournamentId: string, userId: string) => {
  return baseClient.get(`/leaderboard/${tournamentId}/user/${userId}/breakdown`);
};

baseClient.getMyPredictions = async (tournamentId?: string) => {
  const params = tournamentId ? `?tournament_id=${tournamentId}` : '';
  return baseClient.get(`/predictions/my${params}`);
};

baseClient.updateMatchCoefficients = async (tournamentId: string, matchId: string, coefficients: any) => {
  return baseClient.put(`/tournaments/${tournamentId}/matches/${matchId}/coefficients`, coefficients);
};

baseClient.getMatchCoefficients = async (tournamentId: string, matchId: string) => {
  return baseClient.get(`/tournaments/${tournamentId}/matches/${matchId}/coefficients`);
};

baseClient.getMatchOdds = async (tournamentId: string, matchId: string, score?: string) => {
  const params = score ? `?score=${encodeURIComponent(score)}` : '';
  return baseClient.get(`/tournaments/${tournamentId}/matches/${matchId}/odds${params}`);
};

// Leagues API methods
baseClient.getAvailableLeagues = async () => {
  return baseClient.get('/leagues/available');
};

baseClient.getAdminLeagues = async (params?: Record<string, string>) => {
  const query = params ? '?' + new URLSearchParams(params).toString() : '';
  return baseClient.get(`/leagues/admin/all${query}`);
};

baseClient.getAdminLeagueFilterOptions = async () => {
  return baseClient.get('/leagues/admin/filter-options');
};

baseClient.syncLeaguesFromApi = async (mode: string) => {
  return baseClient.post('/leagues/admin/sync', { mode });
};

baseClient.toggleLeagueEnabled = async (leagueId: number, isEnabled: boolean) => {
  return baseClient.patch(`/leagues/${leagueId}/toggle-enabled`, { isEnabled });
};

baseClient.getLeagueDisableImpact = async (leagueId: number) => {
  return baseClient.get(`/leagues/admin/${leagueId}/disable-impact`);
};

// Public league browser (`/matches`) methods
baseClient.getVisibleLeagues = async (params?: VisibleLeaguesQuery) => {
  const search = new URLSearchParams();
  if (params?.search) search.set('search', params.search);
  if (params?.country) search.set('country', params.country);
  if (params?.season) search.set('season', params.season);
  if (params?.limit !== undefined) search.set('limit', String(params.limit));
  if (params?.offset !== undefined) search.set('offset', String(params.offset));
  const query = search.toString();
  return baseClient.get(`/leagues/browsable${query ? `?${query}` : ''}`);
};

baseClient.getVisibleLeagueFilterOptions = async () => {
  return baseClient.get('/leagues/browsable/filter-options');
};

baseClient.getLeague = async (leagueId: string | number) => {
  return baseClient.get(`/leagues/${leagueId}`);
};

baseClient.getLeagueMatches = async (
  leagueId: string | number,
  opts?: { all?: boolean; matchday?: number },
) => {
  let params = '';
  if (opts?.all) {
    params = '?all=true';
  } else if (opts?.matchday) {
    params = `?matchday=${opts.matchday}`;
  }
  return baseClient.get(`/leagues/${leagueId}/matches${params}`);
};

baseClient.getMatchesByDate = async (date: string) => {
  return baseClient.get(`/leagues/matches-by-date?date=${encodeURIComponent(date)}`);
};

baseClient.getLeagueStandings = async (leagueId: string | number) => {
  return baseClient.get(`/leagues/${leagueId}/standings`);
};

baseClient.getLeagueTournaments = async (leagueId: string | number) => {
  return baseClient.get(`/leagues/${leagueId}/tournaments`);
};

baseClient.getMatchOverview = async (matchId: string | number) => {
  return baseClient.get(`/matches/${matchId}/overview`);
};

baseClient.getMatchTournaments = async (matchId: string | number) => {
  return baseClient.get(`/matches/${matchId}/tournaments`);
};

// Bookmakers API methods
baseClient.getAdminBookmakers = async (params?: Record<string, string>) => {
  const query = params ? '?' + new URLSearchParams(params).toString() : '';
  return baseClient.get(`/bookmakers/admin/all${query}`);
};

baseClient.toggleBookmakerEnabled = async (bookmakerId: number, isEnabled: boolean) => {
  return baseClient.patch(`/bookmakers/${bookmakerId}/toggle-enabled`, { isEnabled });
};

// Admin League ↔ Bookmakers mapping methods
baseClient.getAdminLeagueBookmakers = async () => {
  return baseClient.get('/admin/league-bookmakers');
};

baseClient.getAdminLeagueBookmakerDetail = async (leagueId: number) => {
  return baseClient.get(`/admin/league-bookmakers/${leagueId}`);
};

baseClient.saveAdminLeagueBookmakers = async (leagueId: number, bookmakerIds: number[]) => {
  return baseClient.put(`/admin/league-bookmakers/${leagueId}`, { bookmakerIds });
};

baseClient.clearAdminLeagueBookmakers = async (leagueId: number) => {
  return baseClient.delete(`/admin/league-bookmakers/${leagueId}`);
};

baseClient.discoverLeagueBookmakers = async (leagueId: number) => {
  return baseClient.get(`/admin/league-bookmakers/${leagueId}/discover`);
};

// Teams API methods
baseClient.getTeamsByCompetition = async (leagueCode: string) => {
  return baseClient.get(`/teams/competition/${leagueCode}`);
};

baseClient.getTeamById = async (teamId: string) => {
  return baseClient.get(`/teams/${teamId}`);
};

baseClient.getTeamShortNames = async (teamNames: string[]) => {
  return baseClient.post('/teams/shortnames', { teamNames });
};

// Tournament Statistics API methods
baseClient.getTournamentStatistics = async (tournamentId: string, matchday?: number, stage?: string) => {
  const params = new URLSearchParams();
  if (matchday) params.append('matchday', matchday.toString());
  if (stage) params.append('stage', stage);
  const queryString = params.toString();
  return baseClient.get(`/tournaments/${tournamentId}/statistics${queryString ? '?' + queryString : ''}`);
};

// Match Predictions API methods
baseClient.getMatchPredictions = async (tournamentId: string, matchId: string) => {
  return baseClient.get(`/tournaments/${tournamentId}/matches/${matchId}/predictions`);
};

// Admin Settings API methods
baseClient.getAdminSettings = async () => {
  return baseClient.get('/admin/settings');
};

baseClient.updateAdminSettings = async (settings: any) => {
  return baseClient.put('/admin/settings', settings);
};

baseClient.getDebugMode = async () => {
  return baseClient.get('/admin/settings/debug-mode');
};

// Admin match sync methods
baseClient.fullSyncAllMatches = async () => {
  return baseClient.post('/admin/matches/full-sync-all');
};

baseClient.startLeagueFullSync = async (leagueId: number) => {
  return baseClient.post(`/admin/matches/full-sync-league/${leagueId}`);
};

baseClient.getLeagueFullSyncStatus = async () => {
  return baseClient.get('/admin/matches/league-sync-status');
};

// Email verification methods
baseClient.verifyEmail = async (token: string) => {
  return baseClient.get(`/auth/verify-email?token=${token}`);
};

baseClient.resendEmailVerification = async () => {
  return baseClient.post('/auth/resend-verification');
};

baseClient.getVerificationStatus = async () => {
  return baseClient.get('/auth/verification-status');
};

// Password reset methods
baseClient.forgotPassword = async (email: string) => {
  return baseClient.post('/auth/forgot-password', { email });
};

baseClient.validateResetToken = async (token: string) => {
  return baseClient.get(`/auth/validate-reset-token?token=${token}`);
};

baseClient.resetPassword = async (token: string, newPassword: string) => {
  return baseClient.post('/auth/reset-password', { token, newPassword });
};

// Geo data endpoints (public, no auth)
baseClient.getCountries = async () => {
  return baseClient.get('/geo/countries');
};

baseClient.getCities = async (countryIsoCode: string) => {
  return baseClient.get(`/geo/cities?country=${encodeURIComponent(countryIsoCode)}`);
};

baseClient.changePassword = async (currentPassword: string, newPassword: string) => {
  return baseClient.put('/auth/change-password', { currentPassword, newPassword });
};

// Admin user management methods
baseClient.getAdminUsers = async (filters: any = {}) => {
  const params = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key] !== undefined && filters[key] !== null) {
      params.append(key, filters[key].toString());
    }
  });
  const queryString = params.toString();
  return baseClient.get(`/admin/users${queryString ? '?' + queryString : ''}`);
};

baseClient.getAdminUserDetails = async (userId: number) => {
  return baseClient.get(`/admin/users/${userId}`);
};

baseClient.blockUser = async (userId: number, reason: string) => {
  return baseClient.post(`/admin/users/${userId}/block`, { reason });
};

baseClient.unblockUser = async (userId: number) => {
  return baseClient.post(`/admin/users/${userId}/unblock`);
};

baseClient.adminSendVerification = async (userId: number) => {
  return baseClient.post(`/admin/users/${userId}/send-verification`);
};

baseClient.updateUserRole = async (userId: number, role: string, isAdmin: boolean) => {
  return baseClient.post(`/admin/users/${userId}/role`, { role, is_admin: isAdmin });
};

baseClient.getAdminAuditLogs = async (filters: any = {}) => {
  const params = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key] !== undefined && filters[key] !== null) {
      params.append(key, filters[key].toString());
    }
  });
  const queryString = params.toString();
  return baseClient.get(`/admin/audit/actions${queryString ? '?' + queryString : ''}`);
};

baseClient.getEmailVerificationLogs = async (filters: any = {}) => {
  const params = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key] !== undefined && filters[key] !== null) {
      params.append(key, filters[key].toString());
    }
  });
  const queryString = params.toString();
  return baseClient.get(`/admin/audit/email-verification${queryString ? '?' + queryString : ''}`);
};

// User profile management
baseClient.updateUserProfile = async (profileData: any) => {
  return baseClient.put('/auth/profile', profileData);
};

baseClient.uploadAvatar = async (file: File) => {
  const formData = new FormData();
  formData.append('avatar', file);
  return baseClient.post('/user/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

baseClient.removeAvatar = async () => {
  return baseClient.delete('/user/avatar');
};

baseClient.getAdminStats = async () => {
  return baseClient.get('/admin/stats');
};

// OAuth linking methods
baseClient.linkGoogleAccount = async (token: string) => {
  return baseClient.post('/auth/link/google', { token });
};

baseClient.linkAppleAccount = async (identityToken: string, authorizationCode?: string, userData?: any) => {
  return baseClient.post('/auth/link/apple', { 
    identityToken, 
    authorizationCode, 
    userData 
  });
};

baseClient.getOAuthProviders = async () => {
  return baseClient.get('/auth/oauth-providers');
};

baseClient.removeOAuthProvider = async (provider: string) => {
  return baseClient.delete(`/auth/oauth-providers/${provider}`);
};

// Unified account system endpoints
baseClient.checkEmail = async (email: string) => {
  return baseClient.post('/auth/check-email', { email });
};

baseClient.registerTelegramEmail = async (email: string, password: string, telegramData: any) => {
  return baseClient.post('/auth/register-telegram-email', {
    email,
    password,
    telegram_data: telegramData
  });
};

baseClient.linkTelegram = async (email: string, password: string, telegramData: any) => {
  return baseClient.post('/auth/link-telegram', {
    email,
    password,
    telegram_data: telegramData
  });
};

baseClient.getAccountLinks = async () => {
  return baseClient.get('/auth/account-links');
};

baseClient.linkOAuthProvider = async (provider: string, oauthToken: string, email?: string, password?: string) => {
  return baseClient.post(`/auth/link-oauth/${provider}`, {
    oauth_token: oauthToken,
    email,
    password
  });
};

baseClient.unlinkProvider = async (provider: string) => {
  return baseClient.delete(`/auth/unlink/${provider}`);
};

baseClient.deleteAccount = async (password: string) => {
  return baseClient.delete('/auth/account', { data: { password } });
};

baseClient.registerTelegramOnly = async (telegramData: any) => {
  return baseClient.post('/auth/register-telegram', { telegram_data: telegramData });
};

baseClient.bindEmail = async (email: string, password: string) => {
  return baseClient.put('/auth/bind-email', { email, password });
};

// Tournament invitation methods
baseClient.sendEmailInvitation = async (data: EmailInvitationData) => {
  return baseClient.post('/tournament-invites/email', data);
};

baseClient.sendBulkEmailInvitations = async (data: BulkEmailInvitationData) => {
  // Validate data before sending
  if (!data.tournamentId) {
    throw new Error('Tournament ID is required');
  }
  if (!data.emails || !Array.isArray(data.emails) || data.emails.length === 0) {
    throw new Error('Emails array is required');
  }
  if (data.emails.length > 20) {
    throw new Error('Maximum 20 emails per request');
  }
  
  return baseClient.post('/tournament-invites/bulk-email', data);
};

baseClient.sendUsernameInvitation = async (data: UsernameInvitationData) => {
  // Validate data before sending
  if (!data.tournamentId) {
    throw new Error('Tournament ID is required');
  }
  if (!data.inviteeUsername) {
    throw new Error('Username is required');
  }
  
  return baseClient.post('/tournament-invites', {
    tournamentId: data.tournamentId,
    inviteeUsername: data.inviteeUsername,
    message: data.message,
    role: data.role || 'participant'
  });
};

baseClient.getTournamentInvitations = async (tournamentId: number, status?: string) => {
  const params = status ? `?status=${status}` : '';
  return baseClient.get(`/tournament-invites/tournament/${tournamentId}${params}`);
};

baseClient.getInvitationPreview = async (inviteCode: string) => {
  return baseClient.get(`/tournament-invites/${inviteCode}/preview`);
};

baseClient.acceptInvitation = async (inviteCode: string) => {
  return baseClient.post(`/tournament-invites/${inviteCode}/accept`);
};

baseClient.declineInvitation = async (inviteCode: string) => {
  return baseClient.post(`/tournament-invites/${inviteCode}/decline`);
};

baseClient.revokeInvitation = async (inviteCode: string) => {
  return baseClient.post(`/tournament-invites/${inviteCode}/revoke`);
};

// Tournament invite links (link / QR) methods
baseClient.createInviteLink = async (tournamentId: number, data: CreateInviteLinkData) => {
  return baseClient.post(`/tournaments/${tournamentId}/invite-links`, data);
};

baseClient.getInviteLinks = async (tournamentId: number) => {
  return baseClient.get(`/tournaments/${tournamentId}/invite-links`);
};

baseClient.revokeInviteLink = async (tournamentId: number, linkId: number) => {
  return baseClient.post(`/tournaments/${tournamentId}/invite-links/${linkId}/revoke`);
};

baseClient.deleteInviteLink = async (tournamentId: number, linkId: number) => {
  return baseClient.delete(`/tournaments/${tournamentId}/invite-links/${linkId}`);
};

baseClient.getInviteLinkPreview = async (token: string) => {
  return baseClient.get(`/invite-links/${encodeURIComponent(token)}`);
};

baseClient.acceptInviteLink = async (token: string) => {
  return baseClient.post(`/invite-links/${encodeURIComponent(token)}/accept`);
};

// User messages methods
baseClient.getUserMessages = async (options: UserMessagesOptions = {}) => {
  const params = new URLSearchParams();
  
  if (options.messageType) params.append('messageType', options.messageType);
  if (options.isRead !== undefined) params.append('isRead', options.isRead.toString());
  if (options.isArchived !== undefined) params.append('isArchived', options.isArchived.toString());
  if (options.page) params.append('page', options.page.toString());
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.sortBy) params.append('sortBy', options.sortBy);
  if (options.sortOrder) params.append('sortOrder', options.sortOrder);
  
  const queryString = params.toString();
  return baseClient.get(`/user-messages${queryString ? '?' + queryString : ''}`);
};

baseClient.getUnreadMessageCount = async () => {
  return baseClient.get('/user-messages/unread-count');
};

baseClient.getMessage = async (messageId: number) => {
  return baseClient.get(`/user-messages/${messageId}`);
};

baseClient.markMessageAsRead = async (messageId: number) => {
  return baseClient.put(`/user-messages/${messageId}/read`);
};

baseClient.markAllMessagesAsRead = async (messageType?: string) => {
  const data: { messageType?: string } = {};
  if (messageType) data.messageType = messageType;
  
  return baseClient.post('/user-messages/mark-all-read', data);
};

baseClient.archiveMessage = async (messageId: number) => {
  return baseClient.delete(`/user-messages/${messageId}`);
};

// Tournament participants management methods
baseClient.getTournamentParticipantsManagement = async (tournamentId: number, options: ParticipantsManagementOptions = {}) => {
  const params = new URLSearchParams();
  
  if (options.status) params.append('status', options.status);
  if (options.role) params.append('role', options.role);
  if (options.sortBy) params.append('sortBy', options.sortBy);
  if (options.sortOrder) params.append('sortOrder', options.sortOrder);
  if (options.page) params.append('page', options.page.toString());
  if (options.limit) params.append('limit', options.limit.toString());
  
  const queryString = params.toString();
  return baseClient.get(`/tournament-participants/${tournamentId}/participants/management${queryString ? '?' + queryString : ''}`);
};

baseClient.getTournamentParticipantsStats = async (tournamentId: number) => {
  return baseClient.get(`/tournament-participants/${tournamentId}/participants/stats`);
};

baseClient.toggleTournamentParticipantBlock = async (tournamentId: number, userId: number, data: BlockToggleData) => {
  return baseClient.put(`/tournament-participants/${tournamentId}/participants/${userId}/block`, data);
};

baseClient.deleteTournamentParticipant = async (tournamentId: number, userId: number) => {
  return baseClient.delete(`/tournament-participants/${tournamentId}/participants/${userId}`);
};

baseClient.updateParticipantRole = async (tournamentId: number, userId: number, role: string) => {
  return baseClient.put(`/tournament-participants/${tournamentId}/participants/${userId}/role`, { role });
};

// User search methods
baseClient.searchUsers = async (options: UserSearchOptions) => {
  const params = new URLSearchParams();
  
  params.append('q', options.q);
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.exclude && options.exclude.length > 0) {
    params.append('exclude', options.exclude.join(','));
  }
  if (options.tournament_id) params.append('tournament_id', options.tournament_id.toString());
  
  return baseClient.get(`/user-search?${params.toString()}`);
};

export const apiClient = baseClient;
export default { apiClient };